import { getStringFromId } from "../../../helpers/vernacular";
import { VERNACULAR_STRING_IDS } from "constants/vernacularConstants";
import { PAYMENT_FLOWS, PAYMENT_METHODS } from "constants/payment";
import { getPaymentMethodDisabledMessage } from "layouts/PaymentAlert/helpers";
import { cartRefreshTransformer } from "store/payment/transformers/cart/cartRefreshTransformer";
import { paymentCreationInfoTransformer } from "store/payment/transformers/paymentCreationInfoTransformer";
import { paymentMethodOffersTransformer } from "store/payment/transformers/cart/paymentMethodOffersTransformer";
import { vaultParamsWithUserDetailsTransformer } from "store/payment/transformers/wallet/vaultParamsWithUserDetailsTransformer";
import { ItemObjectType } from "store/payment/transformers/cart/itemTransformer";
import { giftCardDataTransformer } from "store/payment/transformers/cart/giftCardDataTransformer";
import { codToPrepaidDataTransformer } from "store/payment/transformers/cart/codToPrepaidDataTransformer";
import { checkIsBeautyPlatform } from "helpers/utils/redirectToCheckoutPage";
import { STORAGE_KEYS } from "../../../constants";
import { logger } from "logging";
import { isAndroid, isIos, isMobileApp } from "helpers/deviceType";
import { AppCallback, doCallAppCallback } from "helpers/utils/webView";
import { CLIENT_URLS, OTHER_URLS } from "constants/urls";
import { doRedirect } from "helpers/utils/redirection";
import { PAYMENT_MODES } from "constants/paymentModes";
import { toLowerCase } from "logics/src/utils/toLowerCase";
import { STATUS_CODE } from "api/constants";
import { formatNumber } from "shared/helpers";
import { formatNumber as formatCurrency } from "helpers/utils/numberFormatter";


export const paymentInfoCallback = async (res: any) => {
  if (res?.data && res?.success) {
    const response = paymentCreationInfoTransformer(res.data);
    const isBinHashValidationRequired = response?.isBinHashValidationRequired;
    const isBinValidationRequired = response?.isBinValidationRequired;
    const isPaytmConnected = response?.isPaytmUser;
    const netBankingList = response?.netBankingList;
    const downBanksList = response?.downBankList;
    const disabledPaymentMethods =
      response?.paymentExtraParamsMap?.disable_payment_methods || [];
    const disabledPaymentMethodMessage =
      response?.paymentExtraParamsMap?.messages.disablePaymentMessage || "";
    const razorPayCustId = response?.razorpayInfoDTO;
    const razorPayKey = response?.razorpayInfoDTO?.razorpayKey;
    const expiryTime = response?.paymentExtraParamsMap?.upiParams?.expiryTime;
    const pollingTimeInSeconds = response?.paymentExtraParamsMap?.upiParams.pollingTime;
    const paymentAlertInfo = response?.paymentAlertInfo;
    const paymentDisabledMessage = getPaymentMethodDisabledMessage(paymentAlertInfo);
    const lastPaymentInfo = response?.lastPaymentInfo;
    const customerVpaList = response?.customerVpaList;
    const upiMapping = response?.paymentExtraParamsMap?.upiMapping || [];
    const cardHashSalt = response?.paymentExtraParamsMap.cardHashSalt;
    const savedCardDTOList = response?.savedCardDTOList;
    const emiInfo = response?.paymentExtraParamsMap.emi;
    const zest = response?.zest;
    const simplDTO = response?.simplDTO;
    const paytmUserDTO = response?.paytmUserDTO;
    const token = response?.token;
    const tokenExpiry = response?.tokenExpiry;
    const sessionRefId = response?.sessionRefId;
    const isCardS2SFlowEnabled = response?.isCardS2SFlowEnabled; //for pci to check if pgfacadepgfacade service is down or not
    const nudge = response?.nudge;
    const isResponseloaded = true;
    const credDetailsDTO = response?.credDetailsDTO;
    const customerId = response?.customerId;
    // used for showing fallback payment option for paymentFailure
    const fallbackModes = response?.fallbackModes;
    const paymentConversionPollTime = response?.paymentExtraParamsMap.paymentConversion.pollingTime;
    const vpaSuffixList = response?.paymentExtraParamsMap?.vpaSuffixList || [];
    const upiIntentQR = response?.paymentExtraParamsMap.upiIntentQR || false;
    const qrPaymentMetaTag = response?.paymentExtraParamsMap?.qrMapping?.qr_payment_meta_tag || '';
    const qrOfferKeys = response?.paymentExtraParamsMap?.qrMapping?.offerKeys || '';
    const qrPackage = response?.paymentExtraParamsMap?.qrMapping?.package || '';


    return {
      isBinHashValidationRequired,
      isBinValidationRequired,
      isPaytmConnected,
      netBankingList,
      downBanksList,
      disabledPaymentMethods,
      disabledPaymentMethodMessage,
      razorPayCustId,
      razorPayKey,
      expiryTime,
      pollingTimeInSeconds,
      paymentAlertInfo,
      paymentDisabledMessage,
      lastPaymentInfo,
      upiMapping,
      customerVpaList,
      savedCardDTOList,
      cardHashSalt,
      emiInfo,
      zest,
      simplDTO,
      paytmUserDTO,
      token,
      tokenExpiry,
      sessionRefId,
      isCardS2SFlowEnabled,
      nudge,
      isResponseloaded,
      credDetailsDTO,
      customerId,
      fallbackModes,
      paymentConversionPollTime,
      vpaSuffixList,
      upiIntentQR,
      qrPaymentMetaTag,
      qrOfferKeys,
      qrPackage
    };
  }
  return null;
};

export const giftCardCallback = async (res: any) => {
  if (res?.data && res?.success) {
    const response = giftCardDataTransformer(res.data);
    const availablePaymentMethods = response?.availablePaymentMethods;

    return {
      availablePaymentMethods,
    };
  }
  return null;
};

export const codToPrepaidCallback = async (res: any) => {
  if (res?.data && res?.success) {
    const response = codToPrepaidDataTransformer(res.data);
    const availablePaymentMethods = response?.availablePaymentMethods;
    const grandTotal = response?.amount;

    return {
      availablePaymentMethods,
      grandTotal
    };
  }
  return null;
}

/**
 * function for setting tracking param into local storage
 * tracking params are being sent to mixopanel on payment success page
 * @param items from cart refresh
 */
 const pushItemTrackingParamsIntoStorage = (items: ItemObjectType[]) => {
  try {
    const trackingParamList = items.map((itemObj: ItemObjectType) =>({
      "productId": itemObj?.productId,
      "trackingParam": itemObj?.trackingParam || {}
    }));
    localStorage.setItem(STORAGE_KEYS.ITEM_TRACKING_PARAM, JSON.stringify(trackingParamList));
  } catch (error) {
    logger.error(error, `Error in pushItemTrackingParamsIntoStorage`);
  }
}

export const cartInfoCallback = async (res: any, isCartRefreshData = false) => {
  if ((res?.data && res?.success) || isCartRefreshData) {
    const data = !isCartRefreshData ? res.data : res;
    const response = cartRefreshTransformer(data);
    const quantity = response?.quantity;
    const itemCount = response?.itemCount;
    if (!quantity) {
      /**
       * incase of webview, redirected to native cart o/w redirected to home page.
       */
      if (isMobileApp()) {
        doCallAppCallback({
          name: AppCallback.DO_REDIRECT_TO_CART,
          data: {}
        })
      } else {
        doRedirect({
          url: OTHER_URLS.HOME_PAGE,
          isClientRedirection: false
        })
      }
      return;
    }
    // cashFree Seamless case
    const paymentGateway = response?.availablePaymentMethodDTO.paymentGateway;
    const disabledPaymentMethodMessage =
      response?.availablePaymentMethodDTO?.disabledPaymentMethodMessage || "";
    const cartTotal = response?.cartTotal;
    const grandTotal = formatNumber(cartTotal?.grandTotal?.amount || 0);
    const subTotalTitle = cartTotal?.subTotal?.title;
    const subTotal = cartTotal?.subTotal?.amount;
    const addressDTO = response?.addressDTO;

    const internationalShippingData = response?.shippingDataDTO;
    const shippingCharge = cartTotal?.shipping?.amount;
    const shippingChargeLimit = response?.shippingDataDTO?.shippingChargeLimit;
    const cartErrorMessage = response?.addressDTO?.error?.errorMessage || response?.error?.errorMessage;

    const cartItems = response?.items;
    let shippingText = response?.shippingDataDTO?.shippingText;
    if (!shippingText) {
      if (shippingCharge > 0) {
        shippingText = getStringFromId(
          VERNACULAR_STRING_IDS.DYNAMIC_SHIPPING_CHARGE,
          `Shipping Charge of Rs.${shippingCharge} will apply on subtotal below Rs.${shippingChargeLimit}`,
          { shippingCharge, shippingLimit: shippingChargeLimit },
        );
      } else {
        shippingText = getStringFromId(
          VERNACULAR_STRING_IDS.DYNAMIC_SHIPPING_CHARGE_FREE,
          `Shipping over order of Rs.${shippingChargeLimit} is free.`,
          { shippingLimit: shippingChargeLimit },
        );
      }
    }
    const paymentDetails = {
      itemCount: response?.itemCount,
      couponCode: response?.couponCode,
      rewardsApplied: response?.rewardsDTO?.rewardsApplied,
      rewardPointsRecieve: response?.rewardsDTO?.rewardPointsRecieve,
      rewardPointsTitle: cartTotal?.rewardPoints?.title,
      rewardPointsAmount: cartTotal?.rewardPoints?.amount,
      appliedOfferLabelDetails: response?.appliedOfferLabelDetails,
      conversionMultiplier: response?.rewardsDTO?.conversionMultiplier,
    };

    const bagTotalTitle = cartTotal?.totalMrp?.title;
    const bagTotal = cartTotal?.totalMrp?.amount;
    const bagDiscountTitle = cartTotal?.cartDiscount?.title;
    const bagDiscount = Math.abs(cartTotal?.cartDiscount?.amount || 0);
    const discountTitle = cartTotal?.ruleDiscount?.title;
    const discount = cartTotal?.ruleDiscount?.amount;
    const couponDiscount = cartTotal?.couponDiscount?.amount;
    const grandTotalTitle = cartTotal?.grandTotal?.title;
    const totalSaved = cartTotal?.totalSaved;
    const ruleDiscountWithoutLabel = cartTotal?.ruleDiscountWithoutLabel?.amount;
    const rewardPointsRecieve = response?.rewardsDTO?.rewardPointsRecieve;
    const customerEmail = response?.addressDTO?.email || "";
    const contactNumber = response?.addressDTO?.contactNumber;
    const isCodAvailable = response?.availablePaymentMethodDTO?.codAvailable;
    const cartOfferKey = response?.availablePaymentMethodDTO?.offerDTO?.offerKey;
    const cartOfferMessage = response?.availablePaymentMethodDTO?.offerDTO?.offerMessage;
    const codMessage = response?.availablePaymentMethodDTO?.codMessage;
    const infoMessage = response?.infoMessageDTO?.infoText;
    const ngsMessage = response?.shippingDataDTO?.shippingConfigMetaData?.ngs?.communicationText

    const isNgsProduct = response?.cartFlagDTO?.hasNgsProduct;
    const availablePaymentMethods = response?.availablePaymentMethodDTO?.paymentMethodList || [];
    // addition of googlePay in availablePaymentMethods
    if (availablePaymentMethods.includes(PAYMENT_METHODS.UPI)) {
      availablePaymentMethods.push(PAYMENT_METHODS.GOOGLE_PAY);
    }
    // tags for products added in cart for segregation at cart and payment
    const paymentTags = response?.paymentTags;
    const items = response?.items || [];
    const currencyCode = response?.currencyCode;
    const isResponseloaded = true;
    const customerId = response?.customerId;
    const pincode = response?.pincode;
    const cartConvenienceChargesBreakupDto = response?.shippingDataDTO?.cartConvenienceChargesBreakupDto;
    const cartId = response.cartId;
    const shippingDataDto = response?.shippingDataDTO;

    pushItemTrackingParamsIntoStorage(items);

    return {
      paymentGateway,
      disabledPaymentMethodMessage,
      grandTotal,
      subTotalTitle,
      bagTotalTitle,
      bagDiscountTitle,
      discountTitle,
      grandTotalTitle,
      bagTotal,
      subTotal,
      bagDiscount,
      discount,
      totalSaved,
      customerEmail,
      rewardPointsRecieve,
      shippingAddress: addressDTO,
      cartErrorMessage,
      shippingText,
      internationalShippingData,
      availablePaymentMethods,
      isCodAvailable,
      cartOfferKey,
      cartOfferMessage,
      codMessage,
      infoMessage,
      ngsMessage,
      isNgsProduct,
      contactNumber,
      paymentDetails,
      couponDiscount,
      shippingCharge,
      quantity,
      itemCount,
      paymentTags,
      cartItems,
      items,
      currencyCode,
      isResponseloaded,
      customerId,
      pincode,
      cartId,
      cartTotal,
      cartConvenienceChargesBreakupDto,
      shippingDataDto,
      ruleDiscountWithoutLabel,
    };
  }
  if (res.statusCode === STATUS_CODE.CART_NOT_FOUND) {
    return cartRefreshTransformer({});
  }
  return null;
};

export const cartOffersCallback = async (res: any) => {
  if (res?.data && res?.success) {
    const response = paymentMethodOffersTransformer(res.data);
    return response;
  }
  return null;
};

export const walletCallback = async (res: any) => {
  if (res?.data && res?.success) {
    const response = vaultParamsWithUserDetailsTransformer(res.data);
    const userDetailsDTO = response?.userDetailsDTO;
    const balance = userDetailsDTO?.walletBalance;
    const isVerificationRequired = userDetailsDTO?.verificationRequired;
    // @types {number<0 | 1>} indicated that semi-closed wallet needs to show(1) or not (0)
    const scWalletFlag = response?.scWalletFlag;
    const scWalletEnabled = userDetailsDTO?.scwalletEnabled && scWalletFlag;
    const contactNumber = userDetailsDTO?.user?.mobileNumber;
    const walletMessages = response?.vaultExtraParamsMap?.messages?.semiCloseWalletMessages;
    const availablePaymentMethods =
      response?.vaultExtraParamsMap?.availablePaymentMethods || [];
    const customerId = userDetailsDTO?.user?.customerId;
    return {
      scWalletFlag,
      scWalletEnabled,
      contactNumber,
      balance,
      walletMessages,
      isVerificationRequired,
      availablePaymentMethods,
      customerId
    };
  }
  return null;
};

export const getAvailableMethodCount = (cartInfo: any) => {
  const availableMethods = cartInfo?.availablePaymentMethods;
  let availableMethodsCount = 0;
  if (Array.isArray(availableMethods)) {
    availableMethodsCount = availableMethods.length;
  }
  return availableMethodsCount;
};

export const getIsSavedMethodsDisplayed = (
  cartInfo: any,
  paymentInfo: any,
  upiIntentList?: any,
  isGPayIntentAvailable = false,
  isAllUpiIntentAvailableOnWeb = false
) => {
  const cartErrorMessage = cartInfo?.cartErrorMessage;
  const grandTotal = cartInfo.grandTotal;
  const isApp = isAndroid() || isIos();
  /**
   * removing cashFreePaymentGateway check as there is no need
   */
  // const cashFreePaymentGateway = get(cartInfo, "paymentGateway");
  let savedCardsDisplayed = false;
  let savedMethodsAvailable: any = [];
  if (grandTotal === 0 || cartErrorMessage) {
    return { savedCardsDisplayed, savedMethodsAvailable };
  }
  const customerVpaList = paymentInfo?.customerVpaList;
  const lastPaymentInfo = paymentInfo?.lastPaymentInfo;
  const savedCardDTOList = paymentInfo?.savedCardDTOList;
  const isPaytmConnected = paymentInfo?.isPaytmConnected;
  const availablePaymentMethods = cartInfo?.availablePaymentMethods;
  const simplDTO = paymentInfo?.simplDTO;
  const installedPSP = upiIntentList?.map((intent: any) => intent?.title);
  const upiMapping = paymentInfo?.upiMapping;
  const upiIntentDetails = upiMapping?.find(
    (upi: any) => upi?.package === lastPaymentInfo?.packageName
  );

  // checking saved vpa list and upi in available methods, enabling flag for saved methods
  if (
    Array.isArray(customerVpaList) &&
    customerVpaList.length &&
    Array.isArray(availablePaymentMethods) &&
    availablePaymentMethods.includes(PAYMENT_MODES.UPI)
  ) {
    savedCardsDisplayed = true;
    savedMethodsAvailable.push(PAYMENT_METHODS.UPI);
  }
  // checking lastpaymentInfo and net banking in available methods, enabling flag for saved methods
  if (
    // !cashFreePaymentGateway &&
    lastPaymentInfo &&
    toLowerCase(lastPaymentInfo.paymentMode) === PAYMENT_MODES.NET_BANKING &&
    Array.isArray(availablePaymentMethods) &&
    availablePaymentMethods.includes(PAYMENT_MODES.NET_BANKING)
  ) {
    savedCardsDisplayed = true;
    savedMethodsAvailable.push(PAYMENT_METHODS.NET_BANKING);
  }
  // checking saved card list and card in available methods, enabling flag for saved methods
  if (
    Array.isArray(savedCardDTOList) &&
    savedCardDTOList.length &&
    Array.isArray(availablePaymentMethods) &&
    availablePaymentMethods.includes(PAYMENT_MODES.CREDIT_CARD)
  ) {
    savedCardsDisplayed = true;
    savedMethodsAvailable.push(PAYMENT_METHODS.CREDIT_CARD);
  }
  // checking paytm connected and paytm in available methods, enabling flag for saved methods
  if (
    // !cashFreePaymentGateway &&
    isPaytmConnected &&
    Array.isArray(availablePaymentMethods) &&
    availablePaymentMethods.includes(PAYMENT_MODES.PAYTM)
  ) {
    savedCardsDisplayed = true;
    savedMethodsAvailable.push(PAYMENT_METHODS.PAYTM);
  }
  if (availablePaymentMethods.includes(PAYMENT_MODES.SIMPL) && Object.keys(simplDTO).length > 0) {
    savedCardsDisplayed = true;
    savedMethodsAvailable.push(PAYMENT_METHODS.SIMPL);
  }
  /**
   * To show the UPI Intent on Android & IOS from last payment info.
   */
  if (
    isApp &&
    Array.isArray(availablePaymentMethods) &&
    availablePaymentMethods.includes(PAYMENT_MODES.INTENT) &&
    toLowerCase(lastPaymentInfo?.paymentMode) === toLowerCase(PAYMENT_MODES.UPI_INTENT) &&
    installedPSP?.includes(upiIntentDetails?.title) &&
    Array.isArray(customerVpaList)
  ) {
    savedCardsDisplayed = true;
    savedMethodsAvailable.push(PAYMENT_MODES.INTENT);
  }
  /**
   * To show the Gpay Intent on MSITE.
   */
  if (
    !isApp &&
    Array.isArray(availablePaymentMethods) &&
    availablePaymentMethods.includes(PAYMENT_MODES.INTENT) &&
    toLowerCase(lastPaymentInfo?.paymentMode) === toLowerCase(PAYMENT_MODES.UPI_INTENT) &&
    lastPaymentInfo?.packageName
  ) {
    
    /**
     * Show only GPay under saved payment options if the UPI Intent experiment is disabled.
     * Explicitly check for the GPay package name to ensure correct detection.
     * Test case: If the user's last payment used UPI Intent with PhonePe,
     * but the Razorpay script detects GPay, we use the package name check to confirm it's actually GPay.
     */
    if (
      isGPayIntentAvailable &&
      lastPaymentInfo?.packageName == "com.google.android.apps.nbu.paisa.user"
    ) {
      savedCardsDisplayed = true;
      savedMethodsAvailable.push(PAYMENT_MODES.INTENT);
    } else if (isAllUpiIntentAvailableOnWeb) {
      savedCardsDisplayed = true;
      savedMethodsAvailable.push(PAYMENT_MODES.INTENT);
    }

  }
  return { savedCardsDisplayed, savedMethodsAvailable };
};

// TODO:- update cartinfocallback
export const setCartItems = (items: ItemObjectType[] ) => {
  let products: any = [];
  if (items?.length) {
    items.forEach((item: ItemObjectType) => {
      let product: any = {
        productName: item?.name,
        onProductNameClick: () => {},
        productImage: {
          url: item?.imageUrl,
        },
        productVariant: {
          size: item?.packSize,
          colorVariantText: item?.shade,
          colorVariantIageSrc: checkIsBeautyPlatform() && item?.variantIcon,
        },
        selectedQuantity: item?.quantity,
        isFreeSample: true,
        showBinIcon: false,
        finalPrice: null,
        originalPrice: null,
      };

      if (item?.cartItemPriceDTO?.price) {
        product.finalPrice = `₹${formatCurrency(Number(item?.cartItemPriceDTO?.price) * Number(item?.quantity))}`;
      }

      if (item?.cartItemPriceDTO?.mrp && item?.cartItemPriceDTO?.catalogDiscountRate > 0) {
        product.originalPrice = `₹${formatCurrency(Number(item?.cartItemPriceDTO?.mrp) * Number(item?.quantity))}`;
      }

      products.push(product);
    });
  }
  return products;
};
/**
 *
 * @param queryString  -- queryString
 * @param key -- key for which value need to be access
 * @returns
 */
export const getAttributeFromQueryString = (queryString: string, key: string) => {
  const searchObject = new URLSearchParams(queryString);
  return searchObject.get(key);
};


/**
 *
 * @param queryString  -- queryString
 * @param key -- key for which value need to be access
 * @returns
 */

export const getPaymentFlow = () =>{
    
    const queryString = window.location.search;
    const query = new URLSearchParams(queryString);
    const flow = query.get("flow");

    if(flow) return flow;
    
    const url = window.location.href;
    
    if(url.includes("/postPayment")){
      return PAYMENT_FLOWS.POST_PAYMENT;
    }else if(url.includes("/paymentSuccess")){
      return PAYMENT_FLOWS.PAYMENT_SUCCESS;
    }else if(url.includes("/preConfirmation")){
      return PAYMENT_FLOWS.PRE_CONFORMATION;
    }else if(url.includes("/mySavedPayment")){
      return PAYMENT_FLOWS.MY_SAVEDPAYMENT;
    }else if(url.includes("/giftCardStatus")){
      return PAYMENT_FLOWS.GIFTCARD_STATUS;
    }else if(url.includes("/pciOtp")){
      return PAYMENT_FLOWS.PCI_OTP;
    }

    return PAYMENT_FLOWS.CHECKOUT;

}

export const onUpiIntentTransactionComplete = (orderId: string) => {
  doRedirect({
    url: `${CLIENT_URLS.POST_PAYMENT_PAGE}?orderId=${orderId}`,
    isClientRedirection: false
  })
}



/**
 * Secondary amount will be sent for wallet balance and gift card balance
 * wallet balance will be 0 if, scWallet is disabled
 */
export const getSecondaryAmount = (scWalletFlag: boolean, walletBalance: number, giftCardBalance: number, getGrandTotal: number) => {
    const walletFinalBalance = getFinalWalletAmount(scWalletFlag, walletBalance);
    let secondaryAmount = 0;
    if (walletFinalBalance && giftCardBalance) {
      secondaryAmount = walletFinalBalance + giftCardBalance;
    } else if (walletFinalBalance) {
      secondaryAmount = walletFinalBalance;
    } else if (giftCardBalance) {
      secondaryAmount = giftCardBalance;
    } else {
      secondaryAmount = 0;
    }

    //walletbalance greater then grandTotal -> 0 || giftcard > grandTotal -> 0
    if(walletFinalBalance >= getGrandTotal || giftCardBalance >= getGrandTotal){
      secondaryAmount = 0;
    }

    //(walletbalance > grandTotal but gifcard < grandTotal) -> giftcard amount;
    if(walletFinalBalance >= getGrandTotal && giftCardBalance < getGrandTotal){
      secondaryAmount = giftCardBalance;
    }

    //(walletbalance < grandTotal but gifcard > grandTotal) -> walletAmount;
    if(walletFinalBalance < getGrandTotal && giftCardBalance >= getGrandTotal){ 
      secondaryAmount = walletFinalBalance;
    }

    return secondaryAmount;
};

/**
 * Returns the final amount based on scwallet flag, if scWalletFlag is disabled then wallet balance will be 0
 * if grandtotal is less than or wallet balance, then secondary amount will be 0
 */
export const getFinalWalletAmount = (scWalletFlag: boolean, walletBalance: number) => {
    let walletAmount = 0;
    if (scWalletFlag && walletBalance) {
      walletAmount = walletBalance;
    } 
    return walletAmount;
};
